import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Sparkles, Play, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle,
  Search, Building2, FileText, ArrowRight, ChevronDown, ChevronUp,
  Database, Wand2, Loader2, Eye, Check, X
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  'Pendente': { color: 'bg-slate-500', icon: Clock },
  'Em Processamento': { color: 'bg-blue-500', icon: RefreshCw },
  'Concluído': { color: 'bg-green-500', icon: CheckCircle },
  'Parcial': { color: 'bg-amber-500', icon: AlertTriangle },
  'Erro': { color: 'bg-red-500', icon: XCircle }
};

export default function EnriquecimentoPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedResultado, setSelectedResultado] = useState(null);
  
  // Form state
  const [filtroDistrito, setFiltroDistrito] = useState('all');
  const [selectedInstituicoes, setSelectedInstituicoes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [creating, setCreating] = useState(false);
  const [executing, setExecuting] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total_jobs: 0,
    em_processamento: 0,
    concluidos: 0,
    instituicoes_enriquecidas: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [jobsRes, distritosRes, instsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enriquecimento/jobs`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instituicoes?limit=500`, { headers })
      ]);
      
      if (jobsRes.ok) {
        const jobsData = await jobsRes.json();
        setJobs(jobsData.jobs || []);
        
        // Calculate stats
        const total = jobsData.jobs?.length || 0;
        const emProc = jobsData.jobs?.filter(j => j.status === 'Em Processamento').length || 0;
        const concl = jobsData.jobs?.filter(j => j.status === 'Concluído').length || 0;
        const instEnr = jobsData.jobs?.reduce((sum, j) => sum + (j.sucesso || 0), 0) || 0;
        
        setStats({
          total_jobs: total,
          em_processamento: emProc,
          concluidos: concl,
          instituicoes_enriquecidas: instEnr
        });
      }
      
      if (distritosRes.ok) {
        setDistritos(await distritosRes.json());
      }
      
      if (instsRes.ok) {
        const instsData = await instsRes.json();
        setInstituicoes(instsData.instituicoes || instsData || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      
      const filtros = {};
      if (filtroDistrito !== 'all') {
        filtros.distrito_id = filtroDistrito;
      }
      if (selectedInstituicoes.length > 0) {
        filtros.instituicao_ids = selectedInstituicoes;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enriquecimento/job`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filtros })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Job criado para ${data.total_instituicoes} instituição(ões)`);
        setCreateModalOpen(false);
        setSelectedInstituicoes([]);
        setFiltroDistrito('all');
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao criar job');
      }
    } catch (error) {
      toast.error('Erro ao criar job');
    } finally {
      setCreating(false);
    }
  };

  const handleExecuteJob = async (jobId) => {
    setExecuting(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enriquecimento/job/${jobId}/executar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Job iniciado com sucesso');
        loadData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao executar job');
      }
    } catch (error) {
      toast.error('Erro ao executar job');
    } finally {
      setExecuting(false);
    }
  };

  const handleViewJobDetails = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enriquecimento/job/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedJob(data);
        setDetailModalOpen(true);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleApplyChange = async (instituicaoId, campo, valor) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enriquecimento/aplicar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instituicao_id: instituicaoId,
          campo,
          valor
        })
      });
      
      if (response.ok) {
        toast.success(`Campo "${campo}" atualizado com sucesso`);
        handleViewJobDetails(selectedJob.id);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao aplicar alteração');
      }
    } catch (error) {
      toast.error('Erro ao aplicar alteração');
    }
  };

  const filteredInstituicoes = instituicoes.filter(inst => {
    if (filtroDistrito !== 'all' && inst.distrito_id !== filtroDistrito) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return inst.nome?.toLowerCase().includes(search) ||
             inst.nif?.includes(search) ||
             inst.codigo_ea?.toLowerCase().includes(search);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="enriquecimento-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="enriquecimento-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            Enriquecimento de Dados com IA
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Enriqueça os dados das instituições através de pesquisa automática e análise IA
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setCreateModalOpen(true)} className="bg-purple-600 hover:bg-purple-700">
            <Wand2 className="w-4 h-4 mr-2" />
            Novo Enriquecimento
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-900">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{stats.total_jobs}</p>
                <p className="text-sm text-slate-500">Total Jobs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.em_processamento}</p>
                <p className="text-sm text-slate-500">Em Processamento</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.concluidos}</p>
                <p className="text-sm text-slate-500">Concluídos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.instituicoes_enriquecidas}</p>
                <p className="text-sm text-slate-500">Enriquecidas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Jobs</CardTitle>
          <CardDescription>Jobs de enriquecimento criados e executados</CardDescription>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">Nenhum job de enriquecimento criado</p>
              <Button onClick={() => setCreateModalOpen(true)}>
                <Wand2 className="w-4 h-4 mr-2" />
                Criar Primeiro Job
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-800">
                  <TableHead>ID</TableHead>
                  <TableHead>Criado Em</TableHead>
                  <TableHead>Instituições</TableHead>
                  <TableHead>Progresso</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG['Pendente'];
                  const StatusIcon = statusConfig.icon;
                  const progress = job.total_instituicoes > 0 
                    ? (job.processadas / job.total_instituicoes) * 100 
                    : 0;
                  
                  return (
                    <TableRow key={job.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell className="font-mono text-sm">{job.id.slice(0, 8)}...</TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(job.created_at).toLocaleString('pt-PT')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{job.total_instituicoes}</span>
                          <span className="text-slate-400 text-sm">
                            ({job.sucesso || 0} sucesso / {job.erros || 0} erros)
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={progress} className="w-24 h-2" />
                          <span className="text-xs text-slate-500">{progress.toFixed(0)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig.color} text-white`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {job.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {job.status === 'Pendente' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleExecuteJob(job.id)}
                              disabled={executing}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleViewJobDetails(job.id)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Job Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-500" />
              Criar Job de Enriquecimento
            </DialogTitle>
            <DialogDescription>
              Selecione as instituições para enriquecer os dados
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Pesquisar por nome, NIF ou código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filtroDistrito} onValueChange={setFiltroDistrito}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrar por distrito" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os distritos</SelectItem>
                  {distritos.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedInstituicoes(filteredInstituicoes.map(i => i.id))}
              >
                Selecionar Todas ({filteredInstituicoes.length})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedInstituicoes([])}
              >
                Limpar Seleção
              </Button>
            </div>

            {/* Institution List */}
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead>NIF</TableHead>
                    <TableHead>Distrito</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInstituicoes.slice(0, 100).map((inst) => (
                    <TableRow 
                      key={inst.id} 
                      className="cursor-pointer"
                      onClick={() => {
                        if (selectedInstituicoes.includes(inst.id)) {
                          setSelectedInstituicoes(prev => prev.filter(id => id !== inst.id));
                        } else {
                          setSelectedInstituicoes(prev => [...prev, inst.id]);
                        }
                      }}
                    >
                      <TableCell>
                        <Checkbox 
                          checked={selectedInstituicoes.includes(inst.id)}
                          onCheckedChange={() => {}}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{inst.nome}</p>
                          <p className="text-xs text-slate-500">{inst.codigo_ea}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{inst.nif || '-'}</TableCell>
                      <TableCell>{inst.distrito_nome || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Summary */}
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <p className="text-sm text-purple-800 dark:text-purple-300">
                <strong>{selectedInstituicoes.length > 0 ? selectedInstituicoes.length : filteredInstituicoes.length}</strong> instituição(ões) serão processadas
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                O processo irá pesquisar dados em bases externas e usar IA para sugerir melhorias
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateJob}
              disabled={creating}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A criar...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Criar Job
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Details Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Job</DialogTitle>
            <DialogDescription>
              ID: {selectedJob?.id}
            </DialogDescription>
          </DialogHeader>

          {selectedJob && (
            <Tabs defaultValue="resultados" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="resultados">Resultados</TabsTrigger>
                <TabsTrigger value="info">Informações</TabsTrigger>
              </TabsList>

              <TabsContent value="resultados" className="space-y-4">
                {selectedJob.resultados?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    {selectedJob.status === 'Pendente' ? (
                      <p>Job ainda não foi executado</p>
                    ) : (
                      <p>Nenhum resultado disponível</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedJob.resultados?.map((resultado, idx) => (
                      <Card key={idx}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              {resultado.instituicao_nome}
                            </CardTitle>
                            <Badge className={resultado.status === 'Concluído' ? 'bg-green-500' : 'bg-red-500'}>
                              {resultado.status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {resultado.diferencas?.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                Diferenças Encontradas:
                              </p>
                              {resultado.diferencas.map((dif, difIdx) => (
                                <div key={difIdx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{dif.campo}</p>
                                    <div className="flex items-center gap-2 text-xs mt-1">
                                      <span className="text-red-500">Atual: {dif.atual || '(vazio)'}</span>
                                      <ArrowRight className="w-3 h-3" />
                                      <span className="text-green-500">Sugerido: {dif.sugerido}</span>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-1">Fonte: {dif.fonte}</p>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleApplyChange(resultado.instituicao_id, dif.campo, dif.sugerido)}
                                    >
                                      <Check className="w-4 h-4 text-green-500" />
                                    </Button>
                                    <Button size="sm" variant="ghost">
                                      <X className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">Nenhuma diferença encontrada</p>
                          )}

                          {resultado.dados_encontrados?.ia && (
                            <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                              <p className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">
                                Sugestões da IA:
                              </p>
                              <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                                {typeof resultado.dados_encontrados.ia === 'string' 
                                  ? resultado.dados_encontrados.ia 
                                  : JSON.stringify(resultado.dados_encontrados.ia, null, 2)}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="info">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={STATUS_CONFIG[selectedJob.status]?.color}>
                      {selectedJob.status}
                    </Badge>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-500">Total Instituições</p>
                    <p className="font-bold">{selectedJob.total_instituicoes}</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-500">Processadas</p>
                    <p className="font-bold text-blue-600">{selectedJob.processadas || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-500">Sucesso / Erros</p>
                    <p className="font-bold">
                      <span className="text-green-600">{selectedJob.sucesso || 0}</span>
                      {' / '}
                      <span className="text-red-600">{selectedJob.erros || 0}</span>
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-500">Criado em</p>
                    <p className="font-medium text-sm">
                      {new Date(selectedJob.created_at).toLocaleString('pt-PT')}
                    </p>
                  </div>
                  {selectedJob.completed_at && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm text-slate-500">Concluído em</p>
                      <p className="font-medium text-sm">
                        {new Date(selectedJob.completed_at).toLocaleString('pt-PT')}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Fechar
            </Button>
            {selectedJob?.status === 'Pendente' && (
              <Button 
                onClick={() => {
                  handleExecuteJob(selectedJob.id);
                  setDetailModalOpen(false);
                }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Play className="w-4 h-4 mr-2" />
                Executar Job
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
